import { getDoc, serverTimestamp } from 'firebase/firestore';
import { auditedSetDoc } from './auditedWrites';
import { getEffectiveTenantId, tenantDoc } from './tenantContext';

export const SESSION_MINUTES_SETUP_COLLECTION = 'sessionMinutesSetup';
export const SESSION_MINUTES_DEFAULTS_DOC_ID = 'defaults';

/**
 * @returns {Promise<Record<string, number>>}
 */
export async function getSessionMinutesDefaultsValues() {
  const tenantId = await getEffectiveTenantId();
  const ref = tenantDoc(tenantId, SESSION_MINUTES_SETUP_COLLECTION, SESSION_MINUTES_DEFAULTS_DOC_ID);
  const snap = await getDoc(ref);
  if (!snap.exists()) return {};
  const raw = snap.data().values;
  if (!raw || typeof raw !== 'object') return {};
  return raw;
}

/**
 * Resolves the configured session minutes for a system/automated encounter type from the
 * tenant's session-minute defaults. Mirrors the Cloud Function resolver: falls back to the
 * generic `system` value, then 0, and clamps to 0-180 WITHOUT throwing (system encounters are
 * fire-and-forget, so a misconfigured default must not drop the encounter).
 * @param {string} encounterType
 * @returns {Promise<number>}
 */
export async function resolveSystemEncounterSessionMinutes(encounterType) {
  const values = await getSessionMinutesDefaultsValues();
  const raw = values?.[encounterType] ?? values?.system ?? 0;
  const n = typeof raw === 'number' ? raw : parseInt(String(raw ?? '').trim(), 10);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(180, Math.round(n)));
}

/**
 * @param {Record<string, number>} values
 * @param {{ id?: string } | null} profile
 */
export async function saveSessionMinutesDefaults(values, profile) {
  const uid = profile?.id || '';
  if (!uid) throw new Error('Not signed in.');
  const tenantId = await getEffectiveTenantId();
  const ref = tenantDoc(tenantId, SESSION_MINUTES_SETUP_COLLECTION, SESSION_MINUTES_DEFAULTS_DOC_ID);
  await auditedSetDoc(
    ref,
    {
      values,
      updatedAt: serverTimestamp(),
      updatedByUid: uid,
    },
    { merge: true },
    { category: 'session_minutes_defaults', entity: 'sessionMinutesSetup' },
  );
}
